import React, { useState, useEffect, useMemo, useRef } from 'react'

import {
	Elements,
	PaymentElement,
	useElements,
	useStripe,
} from '@stripe/react-stripe-js'

import { useTranslation } from '@/core/i18n/useTranslation'
import { Button } from '@/shared/components/atoms/Button'
import { Skeleton } from '@/shared/components/atoms/Skeleton'
import { useNotificationContext } from '@/shared/context/NotificationProvider'
import { useTailwindDarkMode } from '@/shared/hooks/useTailwindDarkMode'
import { extractErrorMessage } from '@/shared/utils/helpers'

import { useStripeCheckout } from '../hooks/useStripeCheckout'
import { useStripePayment } from '../hooks/useStripePayment'

interface StripeCheckoutFormProps {
	accountId: number
	billingSystemCountry?: string
	paymentPlatformMethodId: number
	onSuccess: () => void
	onCancel: () => void
	onSubmittingChange?: (isSubmitting: boolean) => void
	formId?: string
	hideInternalButtons?: boolean
}

const CheckoutFormContent: React.FC<{
	onSuccess: () => void
	onCancel: () => void
	clientSecret: string
	onSubmittingChange?: (isSubmitting: boolean) => void
	formId?: string
	hideInternalButtons?: boolean
}> = ({
	onSuccess,
	onCancel,
	clientSecret,
	onSubmittingChange,
	formId,
	hideInternalButtons,
}) => {
	const { t } = useTranslation('common')
	const stripe = useStripe()
	const elements = useElements()
	const [isSubmitting, setIsSubmitting] = useState(false)
	const [isReady, setIsReady] = useState(false)

	useEffect(() => {
		onSubmittingChange?.(isSubmitting)
	}, [isSubmitting, onSubmittingChange])

	const { confirmPayment } = useStripeCheckout({
		onSuccess,
		clientSecret,
	})

	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault()

		if (!stripe || !elements) {
			return
		}

		setIsSubmitting(true)

		try {
			await confirmPayment()
		} catch (_error) {
			// Error is already handled in the hook
		} finally {
			setIsSubmitting(false)
		}
	}

	if (!stripe || !elements) {
		return (
			<div className="space-y-6 min-h-[550px]">
				<div className="space-y-4">
					<Skeleton className="h-[200px] w-full rounded-lg" />
				</div>
				<div className="flex gap-4 pt-6">
					<Skeleton className="h-10 w-24 rounded-full" />
					<Skeleton className="h-10 w-40 rounded-full" />
				</div>
			</div>
		)
	}

	return (
		<form onSubmit={handleSubmit} className="space-y-6" id={formId}>
			<div className="space-y-4">
				<div className="bg-background border border-border rounded-lg p-4">
					<PaymentElement
						options={{
							layout: 'tabs',
							fields: {
								billingDetails: 'auto',
							},
							terms: {
								card: 'never',
							},
							paymentMethodOrder: ['card'],
							wallets: {
								applePay: 'never',
								googlePay: 'never',
								link: 'never',
							},
						}}
						onReady={() => {
							setIsReady(true)
						}}
					/>
				</div>
			</div>

			{!hideInternalButtons && (
				<div className="flex gap-4 pt-6">
					<Button
						type="button"
						variant="outline"
						onClick={onCancel}
						className="rounded-full cursor-pointer"
						disabled={isSubmitting}
					>
						{t('actions.cancel')}
					</Button>
					<Button
						type="submit"
						className="rounded-full cursor-pointer"
						disabled={!isReady || isSubmitting}
					>
						{isSubmitting
							? t('actions.saving')
							: t('payment_method.save_payment_method')}
					</Button>
				</div>
			)}
		</form>
	)
}

export const StripeCheckoutForm: React.FC<StripeCheckoutFormProps> = ({
	accountId,
	billingSystemCountry,
	paymentPlatformMethodId,
	onSuccess,
	onCancel,
	onSubmittingChange,
	formId,
	hideInternalButtons,
}) => {
	const { stripePromise, createCardPaymentIntent, initializeStripe } =
		useStripePayment({
			accountId,
			billingSystemCountry,
			onError: () => {},
		})
	const [clientSecret, setClientSecret] = useState<string | null>(null)
	const [isLoading, setIsLoading] = useState(true)
	const [isStripeInitialized, setIsStripeInitialized] = useState(false)
	const isDarkMode = useTailwindDarkMode()
	const { showNotification } = useNotificationContext()
	const { t } = useTranslation('common')

	// Use refs to prevent re-initialization
	const hasInitializedStripe = useRef(false)
	const hasFetchedIntent = useRef(false)

	// Initialize Stripe first (only once)
	useEffect(() => {
		if (hasInitializedStripe.current || !accountId || !billingSystemCountry) {
			return
		}

		const initStripe = async () => {
			try {
				hasInitializedStripe.current = true
				await initializeStripe()
				setIsStripeInitialized(true)
			} catch (error: any) {
				hasInitializedStripe.current = false
				showNotification({
					type: 'error',
					message: t('payment_method.save_error'),
					description: extractErrorMessage(
						error,
						t('payment_method.failed_to_initialize_stripe')
					),
				})
				setIsLoading(false)
			}
		}

		initStripe()
	}, [accountId, billingSystemCountry, initializeStripe, showNotification, t])

	// Auto-fetch payment intent when Stripe is initialized (only once)
	useEffect(() => {
		if (
			hasFetchedIntent.current ||
			!isStripeInitialized ||
			!stripePromise ||
			!paymentPlatformMethodId
		) {
			return
		}

		const fetchPaymentIntent = async () => {
			try {
				hasFetchedIntent.current = true
				setIsLoading(true)
				const secret = await createCardPaymentIntent(paymentPlatformMethodId)
				setClientSecret(secret)
			} catch (error: any) {
				hasFetchedIntent.current = false
				showNotification({
					type: 'error',
					message: t('payment_method.save_error'),
					description: extractErrorMessage(
						error,
						t('payment_method.failed_to_initialize_payment_form')
					),
				})
			} finally {
				setIsLoading(false)
			}
		}

		fetchPaymentIntent()
	}, [isStripeInitialized, stripePromise, paymentPlatformMethodId])

	const elementsOptions = useMemo(() => {
		if (!clientSecret) return null

		return {
			clientSecret,
			appearance: {
				theme: (isDarkMode ? 'night' : 'stripe') as 'night' | 'stripe',
			},
		}
	}, [clientSecret, isDarkMode])

	if (
		isLoading ||
		!isStripeInitialized ||
		!stripePromise ||
		!elementsOptions ||
		!clientSecret
	) {
		return (
			<div className="space-y-6 min-h-[550px]">
				<div className="space-y-4">
					<Skeleton className="h-[200px] w-full rounded-lg" />
				</div>
				<div className="flex gap-4 pt-6">
					<Skeleton className="h-10 w-24 rounded-full" />
					<Skeleton className="h-10 w-40 rounded-full" />
				</div>
			</div>
		)
	}

	return (
		<Elements stripe={stripePromise} options={elementsOptions}>
			<CheckoutFormContent
				onSuccess={onSuccess}
				onCancel={onCancel}
				clientSecret={clientSecret}
				onSubmittingChange={onSubmittingChange}
				formId={formId}
				hideInternalButtons={hideInternalButtons}
			/>
		</Elements>
	)
}
